/**
 * ImageGenerationStatus Component
 *
 * Displays progress through multi-step image generation workflow.
 * Shows: Generating prompt → Generating image → Optimizing → Complete
 *
 * Usage:
 * <ImageGenerationStatus
 *   step="generating_image"
 *   promptText="Modern office workspace..."
 *   error="Rate limit exceeded"
 * />
 */

import { useMemo } from 'react';
import { Loader2, CheckCircle2, XCircle, Sparkles, ImageIcon, Settings } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// ============================================
// TYPES
// ============================================

interface ImageGenerationStatusProps {
  step: 'idle' | 'generating_prompt' | 'generating_image' | 'completed' | 'error';
  promptText?: string | null;
  error?: string | null;
  failedStep?: 'prompt' | 'image' | null;
  className?: string;
}

interface Step {
  id: string;
  label: string;
  icon: typeof Sparkles;
}

// ============================================
// CONSTANTS
// ============================================

const STEPS: Step[] = [
  {
    id: 'generating_prompt',
    label: 'Generating prompt',
    icon: Sparkles,
  },
  {
    id: 'generating_image',
    label: 'Generating image',
    icon: ImageIcon,
  },
  {
    id: 'optimizing',
    label: 'Optimizing',
    icon: Settings,
  },
];

// ============================================
// COMPONENT
// ============================================

const ImageGenerationStatus = ({
  step,
  promptText,
  error,
  failedStep,
  className = '',
}: ImageGenerationStatusProps) => {
  // ============================================
  // COMPUTED VALUES
  // ============================================

  const currentStepIndex = useMemo(() => {
    switch (step) {
      case 'generating_prompt':
        return 0;
      case 'generating_image':
        return 1;
      case 'completed':
        return 3; // All steps complete
      case 'error':
        return failedStep === 'prompt' ? 0 : 1;
      default:
        return -1;
    }
  }, [step, failedStep]);

  const showPromptPreview = useMemo(
    () => promptText && (step === 'generating_image' || step === 'completed'),
    [promptText, step]
  );

  // ============================================
  // RENDER IDLE STATE
  // ============================================

  if (step === 'idle') {
    return null;
  }

  // ============================================
  // RENDER ERROR STATE
  // ============================================

  if (step === 'error') {
    return (
      <div className={`border border-destructive rounded-lg p-4 bg-destructive/10 ${className}`}>
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm text-destructive mb-1">Generation Failed</h4>
            <p className="text-sm text-muted-foreground">
              {error || 'An error occurred during image generation'}
            </p>
            {failedStep && (
              <Badge variant="outline" className="mt-2">
                Failed at: {failedStep === 'prompt' ? 'Prompt generation' : 'Image generation'}
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER PROGRESS STATE
  // ============================================

  return (
    <div className={`border rounded-lg p-4 bg-muted/50 ${className}`}>
      {/* Progress steps */}
      <div className="space-y-3">
        {STEPS.map((stepConfig, index) => {
          const Icon = stepConfig.icon;
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex || step === 'completed';

          return (
            <div key={stepConfig.id} className="flex items-center gap-3">
              {/* Icon */}
              <div className="flex-shrink-0">
                {isCompleted ? (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                ) : isActive ? (
                  <Loader2 className="h-5 w-5 text-primary animate-spin" />
                ) : (
                  <Icon className="h-5 w-5 text-muted-foreground" />
                )}
              </div>

              {/* Label */}
              <span
                className={`text-sm ${
                  isActive
                    ? 'font-medium text-foreground'
                    : isCompleted
                      ? 'text-muted-foreground line-through'
                      : 'text-muted-foreground'
                }`}
              >
                {stepConfig.label}
              </span>

              {/* Status badge */}
              {isActive && (
                <Badge variant="default" className="ml-auto">
                  In progress
                </Badge>
              )}
              {isCompleted && (
                <Badge variant="secondary" className="ml-auto">
                  Done
                </Badge>
              )}
            </div>
          );
        })}
      </div>

      {/* Completed state */}
      {step === 'completed' && (
        <div className="mt-4 pt-4 border-t flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
          <span className="text-sm font-medium text-primary">Image generated successfully</span>
        </div>
      )}

      {/* Prompt preview */}
      {showPromptPreview && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Generated Prompt:</h4>
          <p className="text-sm text-foreground leading-relaxed line-clamp-3">{promptText}</p>
        </div>
      )}
    </div>
  );
};

export { ImageGenerationStatus };
